// Catálogo de recursos. Centro Medico → Catálogos → Recursos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Modal, Recurso, Switch, TIPO_RECURSO, fieldCls, med, medRpc } from './shared';

const vacio = { codigo: '', nombre: '', tipo: 'consultorio', ubicacion: '', capacidad: '', color: '', es_activo: true, notas: '' };

export function Recursos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Recurso[]>({ queryKey: ['med-recursos'], queryFn: () => medRpc<Recurso[]>('med_recursos', { _tipo: null }) });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (r?: Recurso) => {
    if (r) setForm({ codigo: r.codigo ?? '', nombre: r.nombre, tipo: r.tipo, ubicacion: r.ubicacion ?? '', capacidad: r.capacidad?.toString() ?? '', color: r.color ?? '', es_activo: r.es_activo, notas: r.notas ?? '' });
    else setForm(vacio);
    setEditId(r?.id ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, tipo: form.tipo, ubicacion: form.ubicacion || null, capacidad: form.capacidad === '' ? null : Number(form.capacidad), color: form.color || null, es_activo: form.es_activo, notas: form.notas || null };
      return editId ? med.update('recurso', editId, body) : med.create('recurso', body);
    },
    onSuccess: () => { toast.success('Recurso guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['med-recursos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('recurso', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Recurso eliminado'); void qc.invalidateQueries({ queryKey: ['med-recursos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Recursos</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo recurso</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Recursos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Ubicación</th><th className="py-2 pr-3">Capacidad</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin recursos</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{r.nombre}</td>
                  <td className="py-2 pr-3 capitalize">{r.tipo}</td>
                  <td className="py-2 pr-3">{r.ubicacion || '—'}</td>
                  <td className="py-2 pr-3">{r.capacidad ?? '—'}</td>
                  <td className="py-2 pr-3">{r.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => abrir(r)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 ml-1" title="Eliminar" aria-label="Eliminar" onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${r.nombre}"?`, { peligro: true }))) return; eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar recurso' : 'Nuevo recurso'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
              <Campo label="Tipo"><select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPO_RECURSO.map((t) => <option key={t} value={t}>{t}</option>)}</select></Campo>
              <Campo label="Ubicación"><input className={fieldCls} value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} /></Campo>
              <Campo label="Capacidad"><input type="number" className={fieldCls} value={form.capacidad} onChange={(e) => setForm({ ...form, capacidad: e.target.value })} /></Campo>
              <Campo label="Color"><input type="color" className={`${fieldCls} h-10 p-1`} value={form.color || '#3b82f6'} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Campo>
            </div>
            <Campo label="Notas"><AutoTextarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
